<template>
  <div class="min-h-screen bg-gradient-to-br from-psychology-calm via-white to-psychology-warm">
    <!-- 背景装饰 -->
    <div class="absolute inset-0 pointer-events-none overflow-hidden">
      <div class="absolute top-32 right-20 w-28 h-28 bg-psychology-accent/5 rounded-full blur-xl"></div>
      <div class="absolute bottom-40 left-16 w-36 h-36 bg-psychology-primary/5 rounded-full blur-xl"></div>
      <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-psychology-secondary/3 rounded-full blur-2xl"></div>
    </div>

    <!-- 导航栏 -->
    <NavBar />

    <!-- 主要内容 -->
    <div class="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <!-- 页面标题 -->
      <div class="text-center mb-12">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-16 h-16 text-psychology-primary mx-auto mb-6">
          <path d="M6 18h8"></path>
          <path d="M3 22h18"></path>
          <path d="M14 22a7 7 0 1 0 0-14h-1"></path>
          <path d="M9 14h2"></path>
          <path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z"></path>
          <path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3"></path>
        </svg>
        <h1 class="text-4xl font-bold text-gray-900 mb-4">科学依据</h1>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto">
          基于严谨的心理学研究和经典理论构建的恋爱占有欲指数评估体系
        </p>
      </div>

      <div class="space-y-8">
        <!-- 理论基础 -->
        <div class="card">
          <div class="p-6">
            <div class="text-2xl font-semibold flex items-center gap-3 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-psychology-primary">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
              理论基础
            </div>

            <div class="grid md:grid-cols-2 gap-6">
              <div class="space-y-4">
                <h3 class="text-lg font-semibold">依恋理论 (Attachment Theory)</h3>
                <p class="text-gray-600 text-sm leading-relaxed">
                  由Bowlby(1969)和Ainsworth(1978)提出的依恋理论，解释了人们在亲密关系中的行为模式。
                  研究表明，不安全依恋类型与较高的占有欲和嫉妒倾向相关，这为理解恋爱占有欲提供了重要的理论框架。
                </p>
                <div class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-yellow-600">
                    <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"></path>
                    <circle cx="12" cy="8" r="6"></circle>
                  </svg>
                  <span class="text-sm font-medium">国际权威理论</span>
                </div>
              </div>

              <div class="space-y-4">
                <h3 class="text-lg font-semibold">浪漫嫉妒研究</h3>
                <p class="text-gray-600 text-sm leading-relaxed">
                  White和Mullen(1989)的浪漫嫉妒三因素模型指出，嫉妒由认知、情绪和行为三个成分构成。
                  占有欲是嫉妒的重要表现形式，反映了个体对关系的控制需求和对失去伴侣的恐惧。
                </p>
                <div class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-green-600">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  <span class="text-sm font-medium">循证支持</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 评估维度 -->
        <div class="card">
          <div class="p-6">
            <div class="text-2xl font-semibold flex items-center gap-3 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-psychology-secondary">
                <path d="M3 3v16a2 2 0 0 0 2 2h16"></path>
                <path d="M18 17V9"></path>
                <path d="M13 17V5"></path>
                <path d="M8 17v-3"></path>
              </svg>
              评估维度
            </div>

            <div class="space-y-4">
              <!-- 控制欲望维度 -->
              <div class="bg-psychology-primary/10 border border-psychology-primary/20 rounded-lg p-4">
                <div class="space-y-3">
                  <div class="flex items-start justify-between">
                    <div>
                      <h4 class="font-semibold">控制欲望维度</h4>
                      <p class="text-sm text-gray-600">Control Desire Dimension</p>·
                    </div>
                    <span class="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 border">10项</span>
                  </div>
                  <p class="text-sm text-gray-600 leading-relaxed">
                    评估在恋爱关系中对伴侣的控制倾向、监控行为和行程掌控需求。包括查看手机、限制社交、要求汇报行程等行为表现。
                  </p>
                  <div class="text-xs text-gray-500">
                    <strong>参考文献：</strong>基于Graham-Kevan & Archer (2003)
                  </div>
                </div>
              </div>

              <!-- 嫉妒强度维度 -->
              <div class="bg-psychology-secondary/10 border border-psychology-secondary/20 rounded-lg p-4">
                <div class="space-y-3">
                  <div class="flex items-start justify-between">
                    <div>
                      <h4 class="font-semibold">嫉妒强度维度</h4>
                      <p class="text-sm text-gray-600">Jealousy Intensity Dimension</p>
                    </div>
                    <span class="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 border">10项</span>
                  </div>
                  <p class="text-sm text-gray-600 leading-relaxed">
                    测量在恋爱关系中的嫉妒情绪强度和排他性倾向。评估对异性接触、前任信息、伴侣称赞他人等情境的情绪反应。
                  </p>
                  <div class="text-xs text-gray-500">
                    <strong>参考文献：</strong>基于Pfeiffer & Wong (1989)
                  </div>
                </div>
              </div>

              <!-- 情感依赖维度 -->
              <div class="bg-psychology-accent/10 border border-psychology-accent/20 rounded-lg p-4">
                <div class="space-y-3">
                  <div class="flex items-start justify-between">
                    <div>
                      <h4 class="font-semibold">情感依赖维度</h4>
                      <p class="text-sm text-gray-600">Emotional Dependency Dimension</p>
                    </div>
                    <span class="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 border">10项</span>
                  </div>
                  <p class="text-sm text-gray-600 leading-relaxed">
                    评估对伴侣的情感依赖程度和独立性水平。包括完整感依赖、情绪依赖、决策依赖等方面的测量。
                  </p>
                  <div class="text-xs text-gray-500">
                    <strong>参考文献：</strong>基于Spann & Fischer (1990)
                  </div>
                </div>
              </div>

              <!-- 关系不安全感维度 -->
              <div class="bg-psychology-warning/10 border border-psychology-warning/20 rounded-lg p-4">
                <div class="space-y-3">
                  <div class="flex items-start justify-between">
                    <div>
                      <h4 class="font-semibold">关系不安全感维度</h4>
                      <p class="text-sm text-gray-600">Relationship Insecurity Dimension</p>
                    </div>
                    <span class="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 border">10项</span>
                  </div>
                  <p class="text-sm text-gray-600 leading-relaxed">
                    测量在亲密关系中的不安全感和焦虑程度。评估被抛弃恐惧、自我价值感、关系确定性等方面的焦虑水平。
                  </p>
                  <div class="text-xs text-gray-500">
                    <strong>参考文献：</strong>基于Brennan et al. (1998)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RPI指数计算方法 -->
        <div class="card">
          <div class="p-6">
            <div class="text-2xl font-semibold flex items-center gap-3 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-psychology-accent">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="6"></circle>
                <circle cx="12" cy="12" r="2"></circle>
              </svg>
              RPI指数计算方法
            </div>

            <div class="space-y-4">
              <div class="bg-gray-50 p-4 rounded-lg">
                <h4 class="font-semibold mb-2">标准化计算流程</h4>
                <div class="text-sm text-gray-600 space-y-2">
                  <p><strong>1. 原始分数收集：</strong>对40道题目（每维度10题）进行5点量表评分</p>
                  <p><strong>2. T分数转换：</strong>将原始分数转换为T分数（均值50，标准差10），确保不同维度的可比性</p>
                  <p><strong>3. 维度权重：</strong>控制欲望、嫉妒强度、情感依赖、关系不安全感四个维度等权重合成</p>
                  <p><strong>4. RPI计算：</strong>将平均T分数映射到0-100的百分位数，得出最终RPI指数</p>
                </div>
              </div>

              <div class="bg-gray-50 p-4 rounded-lg">
                <h4 class="font-semibold mb-2">统计学保证</h4>
                <p class="text-sm text-gray-600">
                  采用基于大样本常模的标准化方法，确保测评结果的信度和效度。
                  RPI指数为百分位数形式，直观反映个体在人群中的相对位置。
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- 双视角评估设计 -->
        <div class="card">
          <div class="p-6">
            <div class="text-2xl font-semibold flex items-center gap-3 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-psychology-secondary">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              双视角评估设计
            </div>

            <div class="grid md:grid-cols-2 gap-6">
              <div class="space-y-4">
                <h4 class="font-semibold">给自己测</h4>
                <p class="text-sm text-gray-600">
                  从第一人称视角评估自己在恋爱关系中的占有欲表现。
                  题目采用"我"的表述，帮助用户进行自我觉察和反思。
                </p>
                <div class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-psychology-primary">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  <span class="text-sm">自我认知增强</span>
                </div>
              </div>

              <div class="space-y-4">
                <h4 class="font-semibold">为恋人测</h4>
                <p class="text-sm text-gray-600">
                  从观察者视角评估恋人表现出的占有欲程度。
                  题目采用"Ta"的表述，基于可观察的行为进行评估。
                </p>
                <div class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-psychology-secondary">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  <span class="text-sm">关系理解深化</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 使用限制与说明 -->
        <div class="card border-psychology-warning/30">
          <div class="p-6">
            <div class="text-2xl font-semibold flex items-center gap-3 mb-6 text-psychology-warning">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
                <path d="M12 9v4"></path>
                <path d="M12 17h.01"></path>
              </svg>
              使用限制与说明
            </div>

            <div class="bg-psychology-warning/5 border border-psychology-warning/20 rounded-lg p-4">
              <div class="space-y-3 text-sm">
                <div class="flex items-start gap-2">
                  <div class="w-1.5 h-1.5 bg-psychology-warning rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>参考工具：</strong>本工具仅供自我了解和关系反思，不构成专业心理诊断或情感咨询</span>
                </div>
                <div class="flex items-start gap-2">
                  <div class="w-1.5 h-1.5 bg-psychology-warning rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>测量误差：</strong>所有心理测量工具都存在测量误差，结果应结合实际情况综合理解</span>
                </div>
                <div class="flex items-start gap-2">
                  <div class="w-1.5 h-1.5 bg-psychology-warning rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>动态变化：</strong>占有欲程度会随关系发展、个人成长和生活经历而变化</span>
                </div>
                <div class="flex items-start gap-2">
                  <div class="w-1.5 h-1.5 bg-psychology-warning rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>文化差异：</strong>恋爱占有欲的表现和接受度在不同文化中可能存在差异</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部操作按钮 -->
        <div class="text-center space-y-4">
          <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
            <router-link to="/guide" class="btn-primary">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5">
                <path d="M12 7v14"></path>
                <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path>
              </svg>
              查看使用指南
            </router-link>
            <router-link to="/" class="btn-secondary">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
              开始测评
            </router-link>
          </div>
          <router-link to="/" class="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
              <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            </svg>
            返回首页
          </router-link>
        </div>
      </div>
    </div>

    <!-- 回到顶部 -->
    <BackToTop />
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import BackToTop from '../components/BackToTop.vue'

export default {
  name: 'SciencePage',
  components: {
    NavBar,
    BackToTop
  }
}
</script>

